/** Pre-computed rigid drag closure (issue #29 follow-up).
 *
 *  Every drag has a fixed cluster of construction lines that travel
 *  with it: the origins themselves, every snap-chain partner, every
 *  offset descendant of any of those, and every snap-chain partner of
 *  those — recursively. The cluster does not change mid-drag because
 *  `Project.constraints` is only mutated inside `ctx.commit`, never
 *  during `onMouseMove`. We therefore compute it once on mouseDown
 *  and translate the same set every frame.
 *
 *  Replaces the per-frame BFS that lived in
 *  `ConstructionTool.liveChainTranslate`, which interleaved seed/queue/
 *  visit bookkeeping with two edge types and accumulated corner-case
 *  bugs each time a new connection topology was added. */

import {
  projectConstructionLines,
  type Project,
} from "./Project";
import { snapEdges, type AnchorRefLite } from "./constraintEnforcement";

/** Forward closure over offset-source edges from `originIds`: every
 *  line whose `offsetSource.sourceId` chains back (directly or
 *  transitively) to any origin. Excludes the origins themselves.
 *  Pure model helper — separate from `constructionOffset.ts`'s
 *  identical helper so this module doesn't transitively load
 *  paperjs-offset (which would prevent unit tests from running in a
 *  node-only vitest environment). */
function reachableOffsetsLocal(
  project: Project,
  originIds: ReadonlyArray<string>,
): Set<string> {
  const out = new Set<string>();
  if (originIds.length === 0) return out;
  const lines = projectConstructionLines(project);
  if (lines.length === 0) return out;
  const childrenBySource = new Map<string, string[]>();
  for (const l of lines) {
    const s = l.offsetSource?.sourceId;
    if (!s) continue;
    if (!childrenBySource.has(s)) childrenBySource.set(s, []);
    childrenBySource.get(s)!.push(l.id);
  }
  const queue: string[] = [...originIds];
  while (queue.length > 0) {
    const cur = queue.shift()!;
    const kids = childrenBySource.get(cur);
    if (!kids) continue;
    for (const k of kids) {
      if (out.has(k)) continue;
      out.add(k);
      queue.push(k);
    }
  }
  return out;
}

/** Issue #180 unification: forward closure over mirror back-refs.
 *  Every line that mirrors-from OR uses one of `originIds` as its
 *  axis chains transitively into the result. Excludes the
 *  origins themselves. Pure model walker (no paperjs-offset
 *  dependency) so this module stays node-testable. */
function reachableMirrorsLocal(
  project: Project,
  originIds: ReadonlyArray<string>,
): Set<string> {
  const out = new Set<string>();
  if (originIds.length === 0) return out;
  const lines = projectConstructionLines(project);
  if (lines.length === 0) return out;
  const dependentsByLine = new Map<string, string[]>();
  for (const l of lines) {
    const link = l.mirrorSource;
    if (!link) continue;
    for (const refId of [link.sourceId, link.axis.constructionId]) {
      if (!dependentsByLine.has(refId)) dependentsByLine.set(refId, []);
      dependentsByLine.get(refId)!.push(l.id);
    }
  }
  const queue: string[] = [...originIds];
  while (queue.length > 0) {
    const cur = queue.shift()!;
    const kids = dependentsByLine.get(cur);
    if (!kids) continue;
    for (const k of kids) {
      if (out.has(k)) continue;
      out.add(k);
      queue.push(k);
    }
  }
  return out;
}

/** Every construction-line id that should rigidly translate with a
 *  drag whose origins are `originLineIds`. Always includes the
 *  origins themselves. Walks the *union* of two graphs in one BFS:
 *
 *    1. Snap edges (undirected) — coincident-constraint partners on
 *       different lines.
 *    2. Offset-forward edges (directed source → derived) — a derived
 *       offset line follows its source rigidly.
 *
 *  The two are interleaved, not run sequentially, because either one
 *  can be the bridge that reaches the next node. Example: A's offset
 *  Ao is snap-pinned to B. Walking snaps from {A} alone never reaches
 *  B (the edge mentions Ao, not A). Walking offsets first lifts Ao
 *  into the set, then the snap step finds B from Ao. Interleaved BFS
 *  handles every ordering by symmetry. */
export function computeDragClosure(
  project: Project,
  originLineIds: ReadonlyArray<string>,
): Set<string> {
  const visited = new Set<string>(originLineIds);
  if (visited.size === 0) return visited;

  const lines = projectConstructionLines(project);
  if (lines.length === 0) return visited;

  // Adjacency map for snap edges (undirected, line-to-line).
  const snapAdj = new Map<string, Set<string>>();
  for (const e of snapEdges(project)) {
    const a = e.a.constructionId;
    const b = e.b.constructionId;
    if (!snapAdj.has(a)) snapAdj.set(a, new Set());
    if (!snapAdj.has(b)) snapAdj.set(b, new Set());
    snapAdj.get(a)!.add(b);
    snapAdj.get(b)!.add(a);
  }

  // Adjacency map for the offset graph, walked UNDIRECTED. A
  // derived line and its source belong to the same rigid cluster:
  // translating either by `(dx, dy)` translates the other by the
  // same delta (the offset relationship preserves translation).
  // So a line that's snap-pinned to a derived offset must drag its
  // source along, and a source that's snap-pinned to anything
  // must drag every sibling offset along — both via the same
  // adjacency. Walking forward-only here would leave a snap-to-
  // offset partner stranded with its source un-translated; commit
  // would then re-derive the offset from the un-moved source and
  // "reset" it to its pre-drag spot, breaking the constraint.
  const offsetAdj = new Map<string, Set<string>>();
  for (const l of lines) {
    const s = l.offsetSource?.sourceId;
    if (!s) continue;
    if (!offsetAdj.has(s)) offsetAdj.set(s, new Set());
    if (!offsetAdj.has(l.id)) offsetAdj.set(l.id, new Set());
    offsetAdj.get(s)!.add(l.id);
    offsetAdj.get(l.id)!.add(s);
  }
  // Mirror adjacency, walked UNDIRECTED (issue #180 unification).
  // A mirror derived line follows BOTH its source AND its axis
  // line — translating any one of the three by the same delta
  // preserves the reflection. Same rationale as the offset case:
  // a snap-pinned-to-mirror must drag the source AND the axis or
  // the commit-time re-derive will snap the mirror back. Kept
  // inline here (not via the connections registry) so this module
  // stays node-only — the Connections registry transitively
  // imports paperjs-offset.
  // Mirror lines have NO drag-closure adjacency (issue #180
  // follow-up). Unlike offsets — which translate together with their
  // source by the SAME delta (translation preserves the offset
  // relationship) — a mirror reflects its source's translation
  // through the axis, so a rigid same-delta translate would land it
  // in the wrong spot. Mirror lines update via the per-frame live
  // re-derive (`Scene.rederiveConnectionsAffectedBy` →
  // `mirrorConnection.rederiveLive`) instead.
  //
  // This also encodes the user's "axis stays put when editing
  // mirrors" requirement: axis lines never appear in any closure
  // walked from source / derived, because no mirror adjacency
  // exists in the closure graph. Bidirectional mirror editing
  // (drag the derived → source moves) is handled in
  // ConstructionTool by re-routing the drag origin to the source
  // root via `resolveMirrorRoot` BEFORE computing the closure — so
  // by the time we get here, the origin is already the source line
  // and the closure is computed for it directly.

  const queue: string[] = [...visited];
  while (queue.length > 0) {
    const cur = queue.shift()!;
    const snapNbrs = snapAdj.get(cur);
    if (snapNbrs) {
      for (const n of snapNbrs) {
        if (visited.has(n)) continue;
        visited.add(n);
        queue.push(n);
      }
    }
    const offsetNbrs = offsetAdj.get(cur);
    if (offsetNbrs) {
      for (const n of offsetNbrs) {
        if (visited.has(n)) continue;
        visited.add(n);
        queue.push(n);
      }
    }
    // (mirror adjacency intentionally absent — see comment above.)
  }
  return visited;
}

/** Vertex-drag specific closure. Differs from `computeDragClosure` in
 *  two ways:
 *
 *    1. The dragged line itself is **not** in the result. Its
 *       geometry is edited segment-by-segment in place; rigidly
 *       translating it on top of the segment edit would double-move
 *       the dragged anchor.
 *    2. The dragged line's offset descendants are also excluded —
 *       they re-derive from the dragged line's *live* pathData each
 *       frame via `Scene.rederiveOffsetsOf`, not via rigid
 *       translation by the frame delta.
 *
 *  The closure is seeded from the dragged anchor's one-hop snap
 *  partner lines, then expanded via the unified snap + offset-forward
 *  BFS just like `computeDragClosure`. The post-walk subtraction of
 *  the dragged line's offset family handles the rare topology where
 *  some partner's chain reaches back into the dragged line's offset
 *  descendants — in that case the offset re-derive wins and the line
 *  is not also translated. */
export function computeVertexDragClosure(
  project: Project,
  draggedAnchor: AnchorRefLite,
): Set<string> {
  const draggedLineId = draggedAnchor.constructionId;
  const edges = snapEdges(project);
  const partnerLines = new Set<string>();
  for (const e of edges) {
    if (
      e.a.kind === "anchor" &&
      e.a.constructionId === draggedLineId &&
      e.a.pathIndex === draggedAnchor.pathIndex &&
      e.a.segmentIndex === draggedAnchor.segmentIndex
    ) {
      if (e.b.constructionId !== draggedLineId) {
        partnerLines.add(e.b.constructionId);
      }
    } else if (
      e.b.kind === "anchor" &&
      e.b.constructionId === draggedLineId &&
      e.b.pathIndex === draggedAnchor.pathIndex &&
      e.b.segmentIndex === draggedAnchor.segmentIndex
    ) {
      if (e.a.constructionId !== draggedLineId) {
        partnerLines.add(e.a.constructionId);
      }
    }
  }
  if (partnerLines.size === 0) return new Set();
  const closure = computeDragClosure(project, [...partnerLines]);
  // Subtract the dragged line and its transitive offset + mirror
  // descendants: those are handled by direct segment edit +
  // `rederiveConnectionsAffectedBy`, not by the rigid-translation
  // pass.
  closure.delete(draggedLineId);
  for (const id of reachableOffsetsLocal(project, [draggedLineId])) {
    closure.delete(id);
  }
  for (const id of reachableMirrorsLocal(project, [draggedLineId])) {
    closure.delete(id);
  }
  return closure;
}

/** Pre-computed at mouseDown for vertex drag: anchors on the dragged
 *  line OTHER than the dragged anchor that are snap-pinned (via a
 *  coincident constraint) to refs on any line in `closureToTranslate`.
 *
 *  Each frame the caller translates each of these anchors by the
 *  frame delta so the coincident pair stays satisfied — the dragged
 *  line distorts to keep the constraint, rather than the constraint
 *  visibly tearing. This is the "chain-through-origin" pull that the
 *  legacy `liveChainTranslate` discovered dynamically per frame; the
 *  closure approach pre-computes it once. */
export function originAnchorPullsForVertexDrag(
  project: Project,
  draggedAnchor: AnchorRefLite,
  closureToTranslate: ReadonlySet<string>,
): Array<{ pathIndex: number; segmentIndex: number }> {
  if (closureToTranslate.size === 0) return [];
  const draggedLineId = draggedAnchor.constructionId;
  const seen = new Set<string>();
  const out: Array<{ pathIndex: number; segmentIndex: number }> = [];
  for (const e of snapEdges(project)) {
    // For each edge: find an anchor-kind endpoint on the dragged
    // line that is NOT the dragged anchor itself, and whose
    // partner's line is in the closure-to-translate set.
    const candidates: Array<{
      onDragged: AnchorRefLite;
      otherLineId: string;
    }> = [];
    if (
      e.a.kind === "anchor" &&
      e.a.constructionId === draggedLineId &&
      !(e.a.pathIndex === draggedAnchor.pathIndex &&
        e.a.segmentIndex === draggedAnchor.segmentIndex)
    ) {
      candidates.push({ onDragged: e.a, otherLineId: e.b.constructionId });
    }
    if (
      e.b.kind === "anchor" &&
      e.b.constructionId === draggedLineId &&
      !(e.b.pathIndex === draggedAnchor.pathIndex &&
        e.b.segmentIndex === draggedAnchor.segmentIndex)
    ) {
      candidates.push({ onDragged: e.b, otherLineId: e.a.constructionId });
    }
    for (const c of candidates) {
      if (!closureToTranslate.has(c.otherLineId)) continue;
      const key = `${c.onDragged.pathIndex}:${c.onDragged.segmentIndex}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        pathIndex: c.onDragged.pathIndex,
        segmentIndex: c.onDragged.segmentIndex,
      });
    }
  }
  return out;
}
